import { z } from "zod";

// AssignmentProposal — "a coach offered you this program/plan; you have
// not answered yet". The row that makes an assignment a PROPOSAL instead
// of a fait accompli (next/inbox-and-notifications.md, decision 2 of
// 19/09: «l'assegnazione è una proposta, l'atleta può rifiutare ⇒ niente
// fan-out immediato»).
//
// Sibling in FORM to CoachAthleteEdge (./coachAthleteEdge.ts) —
// pending → resolved, double consent, the second party's answer is what
// grants anything — but deliberately NOT in shape:
//
//   - an edge is TWO rows (one per partition) because a relationship has
//     two symmetric sides that both read it from their own partition;
//   - a proposal has exactly ONE recipient and one decision, so it is a
//     SINGLE row, in the ASSIGNEE's partition. The proposer does not
//     need a mirror: they already hold the program/plan itself, whose
//     `assignedTo` records who was offered it.
//
// That single-row choice is also what keeps the account-deletion cascade
// honest: pk=USER#<assigneeSub> falls into routes/me.ts's `pk = :pk`-only
// drain on its own, with no second partition to remember (the hole
// LEGAL-6 had to close for edges).
//
// ⛔ The proposal is NOT the program. While it is `pending` the item is
// NOT written into the assignee's partition at all — decision 3, «se non
// accetta, non riceve». The real fan-out (lib/programWrites.ts /
// lib/planWrites.ts) happens on accept and only then.
export const AssignmentKind = z.enum(["program", "plan"]);
export type AssignmentKind = z.infer<typeof AssignmentKind>;

// State machine:
//   - "pending"  : offered, unanswered. The only state GET /me/inbox
//                  derives an item from.
//   - "accepted" : the assignee said yes; the fan-out has run. Kept (not
//                  deleted) so a re-proposal of the same item is an
//                  idempotent overwrite rather than a silent duplicate.
//   - "rejected" : the assignee said no. Kept on purpose: decision 4 of
//                  19/09 — «il rifiuto è visibile al coach» — and the
//                  notice written to the proposer points back at it.
//
// ⛔ There is no "revoked". A proposer who changes their mind removes the
// assignee from the item's `assignedTo`, which is an ordinary save; the
// proposal row is overwritten by the next proposal for the same item.
export const AssignmentProposalStatus = z.enum([
  "pending",
  "accepted",
  "rejected",
]);
export type AssignmentProposalStatus = z.infer<
  typeof AssignmentProposalStatus
>;

// DDB layout: pk=USER#<assigneeSub>, sk=ASSIGNPROP#<kind>#<itemId>
// (built in apps/api/src/lib/ddbKeys.ts). Identity-keyed on
// (kind,itemId) rather than date-keyed on the proposal id — the same
// choice favoriteSk makes, and for the same reason: there is exactly ONE
// live offer of a given item to a given person, so re-proposing is an
// idempotent direct-key Put instead of a second row nobody would ever
// clean up. The uuid `id` survives as an attribute because InboxItem
// needs a stable, unique key for its cursor anchor and its refId.
export const AssignmentProposal = z.object({
  id: z.string().min(1),
  kind: AssignmentKind,
  // The program/plan being offered. Deliberately NOT a copy of it: this
  // row is a pointer, and the item stays in the proposer's partition
  // until the answer is yes.
  itemId: z.string().min(1),
  // ⚠️ Denormalised display values, and the trade-off is declared. The
  // assignee cannot read the item (it isn't in their partition yet), so
  // without these the inbox card would have to say "a coach offered you
  // something". The alternative — one extra read per proposal inside the
  // inbox deriver — is the exact cost routes/inbox.ts already refuses to
  // pay for coach invites ("deliberately no extra read to build one").
  // A rename by the proposer leaves this stale until the next save; the
  // window is a pending proposal's lifetime, and the authoritative name
  // is always the item's own once the answer is yes.
  itemName: z.string().min(1),
  proposerName: z.string().min(1),
  proposerSub: z.string().min(1),
  // CO-22 — the alter ego: coach and athlete can be the SAME sub, so the
  // sub alone can't tell "self-coaching" (no proposal, decision 2 never
  // fires) from "coach assigned to athlete-self" (a real, refusable
  // offer). `.optional().default("coach")` on purpose, ⛔ never
  // `.nullable()`, ⛔ never required — same reasoning as `authoredAs`
  // (program.ts) and `inviterRole` (coachAthleteEdge.ts): inbox.ts's
  // safeParse `continue`s on failure, so a required field would drop
  // every proposal written before this one existed.
  proposerRole: z.enum(["athlete", "coach"]).optional().default("coach"),
  assigneeSub: z.string().min(1),
  status: AssignmentProposalStatus,
  proposedAt: z.iso.datetime(),
  // Absent while `pending`; stamped by the accept/reject transition.
  respondedAt: z.iso.datetime().optional(),
});
export type AssignmentProposal = z.infer<typeof AssignmentProposal>;
